import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import aboutStrings from "../../strings/about";
import Testimony from "../testimonials/testimony";
import Arrow from "../../../assets/svg/arrowRight.svg";
import { getString } from "../../utilities";
import { userRoles } from "../../config";

const AUTO_PLAY_INTERVAL = 8000;

export default function Testimonials({ userRole, className = "" }) {
  const isFamily = userRole === userRoles.family;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [key, setKey] = useState(0);
  const [testimonyHeight, setTestimonyHeight] = useState(0);
  const [quoteHeight, setQuoteHeight] = useState(0);
  const [areTransitionsDisabled, setAreTransitionsDisabled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayInterval = useRef(null);
  const noTransitionDebounce = useRef(null);

  const resetAutoPlay = () => {
    if (autoPlayInterval.current) {
      clearTimeout(autoPlayInterval.current);
    }
    if (!isHovered) {
      autoPlayInterval.current = setTimeout(moveRight, AUTO_PLAY_INTERVAL);
    }
  };

  const getLargestTestimonialsHeights = (node) => {
    const height = node.getBoundingClientRect().height;
    setTestimonyHeight(height);
    const quote = node.getElementsByClassName("testimonials-testimony-quote")[0];
    if (!quote) {
      return;
    }
    setQuoteHeight(quote.getBoundingClientRect().height);
  };

  const disableTransitions = () => {
    setAreTransitionsDisabled(true);
    if (noTransitionDebounce.current) {
      clearTimeout(noTransitionDebounce.current);
    }
    if (autoPlayInterval.current) {
      clearTimeout(autoPlayInterval.current);
    }
    noTransitionDebounce.current = setTimeout(() => {
      setAreTransitionsDisabled(false);
    }, 300);
  };

  const largestTestimonialRef = useCallback((node) => {
    if (!node) {
      return;
    }
    if (!document.fonts) {
      return;
    }
    const observer = new ResizeObserver(() => {
      getLargestTestimonialsHeights(node);
      disableTransitions();
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const [testimonialData, largestTestimonial] = useMemo(() => {
    const { data } = aboutStrings.testimonials;
    const nextTestimonialData = [];
    let largestTestimonial = data[0];
    let maxLength = 0;
    data.forEach((datum) => {
      if (datum.isFamily === isFamily) {
        nextTestimonialData.push(datum);
        if (datum.quote.length > maxLength) {
          largestTestimonial = datum;
          maxLength = datum.quote.length;
        }
      }
    });
    return [nextTestimonialData, largestTestimonial];
  }, [userRole]);

  const getModuloOfNegative = (i, n) => {
    return ((i % n) + n) % n;
  };

  const renderTestimonials = () => {
    return testimonials.map((testimony) => {
      const { offset, key, ...testimonyProps } = testimony;
      return (
        <div
          className={`testimony-container${offset === 0 ? " active" : ""}${areTransitionsDisabled ? " transitions-disabled" : ""}`}
          key={key}
          {...(offset === 0 && { "aria-live": "polite" })}
        >
          <Testimony data={testimonyProps} offset={offset} />;
        </div>
      );
    });
  };

  const initializeTestimonials = () => {
    const nextTestimonials = [];
    let tempKey = key;
    for (let i = -2; i < 0; ++i, ++tempKey) {
      const index = getModuloOfNegative(currentIndex + i, testimonialData.length);
      const testimony = testimonialData[index];
      nextTestimonials.push({ ...testimony, offset: i, key: tempKey });
    }
    const index = getModuloOfNegative(currentIndex, testimonialData.length);
    const testimony = testimonialData[index];
    nextTestimonials.push({ ...testimony, offset: 0, key: tempKey });
    ++tempKey;
    for (let i = 1; i <= 2; ++i, ++tempKey) {
      const index = (currentIndex + i) % testimonialData.length;
      const testimony = testimonialData[index];
      nextTestimonials.push({ ...testimony, offset: i, key: tempKey });
    }
    setTestimonials(nextTestimonials);
    setKey(tempKey);
  };

  // This function facilitates CSS transition animation.
  // Offset is a CSS variable that determines positioning of each element.
  // Content is rendered in stages to ensure that all elements needed for movement are in the DOM at the beginning of an animation,
  // then rendered again with appropriate offsets.

  // Steps:
  // 1. Set data for all testimonials that will be rendered throughout the animation, with initial offsets.
  // 2. Set data for all testimonials that will be rendered throughout the animation, with final offsets.
  // 3. CSS animates transition due to difference between initial and final offsets.
  // 4. Cleanup extra elements after animation completes so that DOM doesn't grow out of control with every movement.

  // The key here is not to simply set testimonial state in sequence—this results in React batching updates,
  // which results in a single render of testimonials in their final positions.
  // Without two rendered states—without a delta in position between them—CSS animation cannot function.
  // To ensure that state changes result in two renders, you have to request an animation frame to do something after then first render.
  // Nested request animation frame is needed, as the first request causes something to be done before rendering the next frame.
  // The nested request results in rendered final state of testimonials after rendering the next frame.
  const moveTestimonials = (distance, direction) => {
    if (document.hidden) {
      return;
    }
    const nextTestimonials = [];

    let tempKey = 0;
    if (direction === "right") {
      nextTestimonials.push(...testimonials);
      for (let i = 1; i <= distance; ++i, ++tempKey) {
        const index = (currentIndex + 2 + i) % testimonialData.length;
        const testimony = testimonialData[index];
        nextTestimonials.push({ ...testimony, offset: i + 2, key: tempKey + key });
      }
    } else {
      for (let i = -distance; i < 0; ++i, ++tempKey) {
        const index = getModuloOfNegative(currentIndex - 2 + i, testimonialData.length);
        const testimony = testimonialData[index];
        nextTestimonials.push({ ...testimony, offset: i - 2, key: tempKey + key });
      }
      nextTestimonials.push(...testimonials);
    }
    setKey((prev) => prev + tempKey);
    setTestimonials(nextTestimonials);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTestimonials((prev) => {
          const nextTestimonials = [...prev];
          return nextTestimonials.map((testimony) => {
            const { offset, ...testimonyProps } = testimony;
            if (direction === "right") {
              return { ...testimonyProps, offset: offset - distance };
            } else {
              return { ...testimonyProps, offset: offset + distance };
            }
          });
        });
        setCurrentIndex((prev) =>
          direction === "right"
            ? (prev + distance) % testimonialData.length
            : getModuloOfNegative(prev - distance, testimonialData.length),
        );
      });
    });
    setTimeout(() => {
      setTestimonials((prev) =>
        direction === "right" ? prev.slice(distance, prev.length) : prev.slice(0, prev.length - distance),
      );
    }, 500);
  };

  useEffect(() => {
    initializeTestimonials();
  }, [testimonialData]);

  const moveRight = () => {
    moveTestimonials(1, "right");
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (autoPlayInterval.current) {
          clearTimeout(autoPlayInterval.current);
        }
      } else {
        resetAutoPlay();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    if (!areTransitionsDisabled && !isHovered) {
      autoPlayInterval.current = setTimeout(moveRight, AUTO_PLAY_INTERVAL);
    }

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      clearTimeout(autoPlayInterval.current);
    };
  }, [testimonials, areTransitionsDisabled]);

  const goForward = () => {
    moveTestimonials(1, "right");
    resetAutoPlay();
  };

  const goBack = () => {
    moveTestimonials(1, "left");
    resetAutoPlay();
  };

  const onHover = () => {
    setIsHovered(true);
    if (autoPlayInterval.current) {
      clearTimeout(autoPlayInterval.current);
    }
  };

  const onMouseExit = () => {
    setIsHovered(false);
    autoPlayInterval.current = setTimeout(moveRight, AUTO_PLAY_INTERVAL);
  };

  const onClickDot = (index) => {
    if (index === currentIndex) {
      return;
    }
    const direction = currentIndex < index ? "right" : "left";
    moveTestimonials(Math.abs(index - currentIndex), direction);
    resetAutoPlay();
  };

  const renderDots = () => {
    const buttons = [];
    for (let i = 0; i < testimonialData.length; ++i) {
      buttons.push(
        <button
          key={testimonialData[i].source.name}
          className={`carousel-button ${currentIndex === i ? "active" : ""}`}
          onClick={() => onClickDot(i)}
        />,
      );
    }
    return <div className="carousel-button-container">{buttons}</div>;
  };

  return (
    <div className={`subscribe-testimonials ${className}`}>
      <h2>{getString("testimonials.subscribe.description", { strings: aboutStrings })}</h2>
      <div className="largest-testimonial">
        <div className="testimony-container" ref={largestTestimonialRef}>
          <Testimony data={largestTestimonial} />
        </div>
      </div>
      <div
        className="testimonials-carousel"
        style={{ "--testimony-height": `${testimonyHeight}px`, "--quote-height": `${quoteHeight}px` }}
        onMouseEnter={onHover}
        onMouseLeave={onMouseExit}
      >
        {renderTestimonials()}
        <button className="prev-button" onClick={goBack}>
          <Arrow />
        </button>
        <button className="next-button" onClick={goForward}>
          <Arrow />
        </button>
      </div>
      {renderDots()}
    </div>
  );
}
